import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  Divider,
  Collapse,
  IconButton,
  useTheme,
  alpha,
  Drawer
} from "@mui/material";
import {
  AccountBalance,
  AttachMoney,
  Schedule,
  TrendingUp,
  Business,
  Description,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  Assignment,
  ShowChart
} from "@mui/icons-material";
import {
  formatCurrency,
  formatHandlerCode,
  formatIsoDate,
  formatPercentage
} from "../../../utils/namesFormater";
import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { calculateRemainingMonths } from "../../../utils/dateCalculators";
import {
  formatInterestType,
  formatLinkageType,
  formatLoanToPolicyRatio,
  formatProductType,
  formatRepaymentFrequency,
  formatRepaymentType
} from "../../../utils/enumFormatter";
import { MimshakService } from "../../../services/mimshakService";
import { fundService } from "../../../services/fundService";
import { ProductFundYieldsChart } from "../Charts/ProductFundYieldsChart";

interface Props {
  product: Product;
}

const LoansCard: React.FC<Props> = ({ product }) => {
  const theme = useTheme();
  const [expandedLoans, setExpandedLoans] = useState<{ [key: string]: boolean }>({});
const [showFundChart, setShowFundChart] = useState(false);
const { pathCodes, avgYields } = useMemo(() => {
      const pathCodes = MimshakService.extractPathCodesFromProducts([product]);
      const fundData = fundService.getFundsFromCacheByIds(pathCodes);
      const fundMap = new Map<number, number>();

      fundData.forEach(f => {
        if (f.fund_id != null && f.avg_annual_yield_trailing_3yrs != null) {
          fundMap.set(f.fund_id, f.avg_annual_yield_trailing_3yrs);
        }
      });

      const avgYields = pathCodes
        .map(code => ({
          fundId: code,
          yield: fundMap.get(code)
        }))
        .filter(f => f.yield !== undefined);

      return { pathCodes, avgYields };
    }, [product]);
  const toggleExpanded = (loanKey: string) => {
    setExpandedLoans(prev => ({
      ...prev,
      [loanKey]: !prev[loanKey]
    }));
  };
const selectedFundIds = useMemo(() => pathCodes, [pathCodes]);
  const getProgressPercentage = (startDate: string, endDate: string, remainingMonths: number) => {
    const totalMonths = calculateRemainingMonths(startDate, endDate || "");
    if (!totalMonths || totalMonths <= 0) return 0;
    return ((totalMonths - remainingMonths) / totalMonths) * 100;
  };

  const getStatusColor = (monthsLeft: number) => {
    if (monthsLeft <= 6) return { color: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.1) };
    if (monthsLeft <= 12) return { color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.1) };
    return { color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.1) };
  };

  const getStatusIcon = (monthsLeft: number) => {
    if (monthsLeft <= 6) return <Warning />;
    return <CheckCircle />;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        הלוואות פעילות
      </Typography>

      {product.policies_or_accounts?.flatMap((policy, policyIdx) =>
        policy.loan_details?.map((loan, loanIdx) => {
          const updateDate = policy.update_date;
          const loanKey = `${policyIdx}-${loanIdx}`;
          const remainingMonths = calculateRemainingMonths(updateDate, loan.loan_end_date || "");
          const totalMonths = calculateRemainingMonths(loan.loan_start_date, loan.loan_end_date || "");
          const progressPercentage = getProgressPercentage(loan.loan_start_date, loan.loan_end_date || "", remainingMonths);
          const statusColors = getStatusColor(remainingMonths);
          const isExpanded = expandedLoans[loanKey] || false;

          return (
            <Card 
              key={loanKey} 
              sx={{ 
                mb: 3, 
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 3,
                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: 'white',
                  p: 3
                }}
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.common.white, 0.2),
                          p: 1.5,
                          borderRadius: '50%'
                        }}
                      >
                        <Business />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          הלוואה פעילה
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                          {formatHandlerCode(product.details.handler_code)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box textAlign="right">
                      <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                        מספר פוליסה
                      </Typography>
                      <Typography variant="h6" fontFamily="monospace">
                        {policy.policy_number}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Product Type Badge */}
                <Chip
                  icon={<Description />}
                  label={formatProductType(product.details.product_type)}
                  sx={{
                    mb: 3,
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                    fontWeight: 'medium'
                  }}
                />

                {/* Key Metrics */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Loan Amount */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        p: 2
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <AttachMoney sx={{ color: theme.palette.success.main }} />
                          <Typography variant="body2" fontWeight="medium" color={theme.palette.success.dark}>
                            סכום ההלוואה
                          </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color={theme.palette.success.dark}>
                          {formatCurrency(loan.loan_amount)}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Remaining Balance */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        p: 2
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <TrendingUp sx={{ color: theme.palette.warning.main }} />
                          <Typography variant="body2" fontWeight="medium" color={theme.palette.warning.dark}>
                            יתרה לתשלום
                          </Typography>
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color={theme.palette.warning.dark}>
                          {formatCurrency(loan.loan_balance)}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                {/* Progress Bar */}
                <Box sx={{ mb: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" fontWeight="medium">
                      התקדמות החזר
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(progressPercentage)}% הושלם
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[500], 0.2),
                      '& .MuiLinearProgress-bar': {
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        borderRadius: 4
                      }
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                      סיום: {formatIsoDate(loan.loan_end_date)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      התחלה: {formatIsoDate(loan.loan_start_date)}
                    </Typography>
                  
                  </Box>
                </Box>

                {/* Status and Interest Rate */}
                <Card sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05), p: 2, mb: 2 }}>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                      <Chip
                        icon={getStatusIcon(remainingMonths)}
                        label={`${remainingMonths} חודשים נותרו`}
                        sx={{
                          bgcolor: statusColors.bg,
                          color: statusColors.color,
                          fontWeight: 'medium'
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                        <Schedule />
                        <Typography variant="body2">
                          ריבית: {formatPercentage(loan.interest_rate)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>

                {/* Interest Details Strip */}
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    p: 2.5,
                    mb: 2
                  }}
                >
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalance sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            ריבית
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {formatPercentage(loan.interest_rate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                          סוג ריבית
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {formatInterestType(loan.interest_type)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                          סוג הצמדה
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {formatLinkageType(loan.linkage_type)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>

                {/* Average Annual Return - Highlighted */}
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    color: 'white',
                    p: 3,
                    mb: 3
                  }}
                >
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            bgcolor: alpha(theme.palette.common.white, 0.2),
                            p: 1,
                            borderRadius: '50%'
                          }}
                        >
                          <ShowChart />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
                            תשואה שנתית ממוצעת
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.7) }}>
                            3 שנים אחרונות
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                   <Grid item >
                      <Box textAlign="right">
                        {avgYields.length > 0 ? (
                          avgYields.map(({ fundId, yield: y }) => (
                            <Typography key={fundId} variant="h6" fontWeight="bold">
                              קוד קרן {fundId}: {formatPercentage(y!)}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="h6">אין נתונים</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>

                {/* Expandable Details Button */}
                <Button
                  fullWidth
                  onClick={() => toggleExpanded(loanKey)}
                  endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.primary.main,
                    py: 1.5,
                    mb: 2,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`
                    }
                  }}
                >
                  {isExpanded ? 'הסתר פרטים נוספים' : 'הצג פרטים נוספים'}
                </Button>

                {/* Expandable Details */}
                <Collapse in={isExpanded}>
                  <Card sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05), p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      {[
                        { label: "סוג ריבית", value: formatInterestType(loan.interest_type) },
                        { label: "סוג הצמדה", value: formatLinkageType(loan.linkage_type) },
                        { label: "סוג החזר", value: formatRepaymentType(loan.repayment_type) },
                        { label: "תדירות החזר", value: formatRepaymentFrequency(loan.repayment_frequency) },
                        { label: "החזר תקופתי", value: formatCurrency(loan.periodic_repayment_amount) },
                        { label: "יחס הלוואה לפוליסה", value: formatLoanToPolicyRatio(loan.loan_to_policy_ratio) },
                        { label: "סה\"ח חודשי הלוואה", value: totalMonths },
                        { label: "תקופת הלוואה (חודשים)", value: loan.loan_period_months }
                      ].map((field, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            {field.label}:
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {field.value ?? "-"}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                </Collapse>

                {/* Action Buttons */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Assignment />}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 'medium'
                      }}
                    >
                      פרטי תשלום
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ShowChart />}
                       onClick={() => setShowFundChart(true)}
                      sx={{
                        borderColor: alpha(theme.palette.grey[500], 0.3),
                        color: theme.palette.text.primary,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 'medium',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      תשואות במוצר
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
              <Drawer
              anchor="bottom"
              open={showFundChart}
              onClose={() => setShowFundChart(false)}
            >
              <Box sx={{ p: 4, minHeight: 300 }}>
                <ProductFundYieldsChart fundPathCodes={selectedFundIds} />
              </Box>
            </Drawer>
            </Card>
          );
        }) ?? []
      )}
    </Box>
  );
};

export default LoansCard;